import { X, ExternalLink, Info, AlertTriangle, CheckCircle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useGlobalAnnouncements, GlobalAnnouncement } from '@/hooks/useGlobalAnnouncements';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const variantConfig = {
  info: {
    bg: 'bg-blue-600 dark:bg-blue-700',
    text: 'text-white',
    icon: Info,
    buttonVariant: 'bg-white/20 hover:bg-white/30 text-white',
  },
  warning: {
    bg: 'bg-amber-500 dark:bg-amber-600',
    text: 'text-white',
    icon: AlertTriangle,
    buttonVariant: 'bg-white/20 hover:bg-white/30 text-white',
  },
  success: {
    bg: 'bg-emerald-600 dark:bg-emerald-700',
    text: 'text-white',
    icon: CheckCircle,
    buttonVariant: 'bg-white/20 hover:bg-white/30 text-white',
  },
  promo: {
    bg: 'bg-gradient-to-r from-primary to-purple-600',
    text: 'text-white',
    icon: Sparkles,
    buttonVariant: 'bg-white/20 hover:bg-white/30 text-white',
  },
};

function AnnouncementBanner({
  announcement,
  onDismiss,
  onHeightChange,
}: {
  announcement: GlobalAnnouncement;
  onDismiss: () => void;
  onHeightChange: (height: number) => void;
}) {
  const config = variantConfig[announcement.variant] || variantConfig.info;
  const Icon = config.icon;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current) {
      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          onHeightChange(entry.contentRect.height);
        }
      });
      observer.observe(ref.current);
      // Initial height
      onHeightChange(ref.current.offsetHeight);
      return () => observer.disconnect();
    }
  }, [onHeightChange]);

  return (
    <motion.div
      ref={ref}
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ duration: 0.2 }}
      className={cn(config.bg, config.text, 'overflow-hidden')}
    >
      <div className="container mx-auto px-3 py-1 relative">
        <div className="flex items-center justify-center gap-2">
          <Icon className="h-3.5 w-3.5 shrink-0" />
          <span className="font-medium text-xs">{announcement.title}</span>
          {announcement.message && (
            <span className="text-xs opacity-90 hidden sm:inline">
              — {announcement.message}
            </span>
          )}
          {announcement.link && (
            <a
              href={announcement.link}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-colors ml-1',
                config.buttonVariant
              )}
            >
              {announcement.link_text || 'Learn More'}
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>

        {announcement.is_dismissible && (
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 text-white/80 hover:text-white hover:bg-white/20 absolute right-3 top-1/2 -translate-y-1/2"
            onClick={onDismiss}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>
    </motion.div>
  );
}

export function GlobalAnnouncementBanner() {
  const { announcements, dismissAnnouncement } = useGlobalAnnouncements();
  const location = useLocation();

  // Don't render on admin pages
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Only show the first active announcement
  const currentAnnouncement = isAdminRoute ? null : announcements[0];

  // Update CSS custom property for header offset
  const updateHeaderOffset = (height: number) => {
    document.documentElement.style.setProperty('--announcement-banner-height', `${height}px`);
  };

  // Reset height when no announcement or on admin routes
  useEffect(() => {
    if (!currentAnnouncement || isAdminRoute) {
      document.documentElement.style.setProperty('--announcement-banner-height', '0px');
    }
  }, [currentAnnouncement, isAdminRoute]);

  return (
    <div className="fixed top-0 left-0 right-0 z-[60]">
      <AnimatePresence>
        {currentAnnouncement && (
          <AnnouncementBanner
            key={currentAnnouncement.id}
            announcement={currentAnnouncement}
            onDismiss={() => dismissAnnouncement(currentAnnouncement.id)}
            onHeightChange={updateHeaderOffset}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
